import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// canonical (English) values stored in the backend, regardless of UI language
const PROJECT_VALUES = ["Product Reveal", "Brand Film", "Social Ad Series", "Full Campaign"];

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "", project_type: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.errFill);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.okTitle, { description: t.contact.okDesc });
      setForm({ name: "", email: "", message: "", project_type: "" });
    } catch (err) {
      toast.error(t.contact.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative z-10 mx-auto max-w-7xl px-6 py-28 sm:py-36" data-testid="contact">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-3 text-xs uppercase tracking-[0.25em] text-white/50">{t.contact.eyebrow}</p>
          <h2 className="font-anton text-5xl sm:text-7xl leading-[0.9] tracking-tightest">
            {t.contact.heading1}<br />{t.contact.heading2}
          </h2>
          <p className="mt-8 max-w-sm text-base text-white/60">
            {t.contact.desc}
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="glass rounded-2xl p-8 sm:p-10"
          data-testid="contact-form"
        >
          <div className="space-y-8">
            <input className="line-input w-full pb-3 text-lg" placeholder={t.contact.name}
              value={form.name} onChange={set("name")} data-testid="input-name" />
            <input className="line-input w-full pb-3 text-lg" placeholder={t.contact.email} type="email"
              value={form.email} onChange={set("email")} data-testid="input-email" />

            <div>
              <p className="mb-4 text-xs uppercase tracking-[0.2em] text-white/40">{t.contact.projectType}</p>
              <div className="flex flex-wrap gap-3">
                {PROJECT_VALUES.map((value, idx) => (
                  <button
                    type="button"
                    key={value}
                    onClick={() => setForm((f) => ({ ...f, project_type: value }))}
                    className={`rounded-full border px-4 py-2 text-sm transition-all ${
                      form.project_type === value
                        ? "border-white bg-white text-black"
                        : "border-white/20 text-white/70 hover:border-white/50"
                    }`}
                    data-testid={`project-type-${value.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {t.contact.projectTypes[idx]}
                  </button>
                ))}
              </div>
            </div>

            <textarea className="line-input w-full resize-none pb-3 text-lg" rows={3} placeholder={t.contact.message}
              value={form.message} onChange={set("message")} data-testid="input-message" />

            <button
              type="submit"
              disabled={loading}
              className="group flex w-full items-center justify-center gap-2 rounded-full bg-white py-4 font-semibold text-black transition-all hover:bg-white/80 disabled:opacity-60"
              data-testid="submit-contact"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t.contact.send}
              {!loading && <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
